import { usePostHog as usePostHogBase } from 'posthog-js/react';

import { config } from '~/config';
import { useLanguageStore } from '~/stores/use-language-store/useLanguageStore';

import { PosthogTrackingEventName, PosthogTrackingSource } from './constants';

export type CaptureUserProperties = {
  properties: {
    user_role?: string;
    user_firm_id?: string;
    user_created_at?: string;
    user_on_trial?: boolean;
    user_subscription?: string;
    user_subscription_next_payment?: string;
  };
  setOnce?: boolean;
};

export const usePostHog = () => {
  const posthog = usePostHogBase();
  const shouldEnableTracking = ['staging', 'prod'].includes(
    config.app.environment
  );
  const locale = useLanguageStore(state => state.languageSetting);

  const identifyUser = (userId: string) => {
    if (shouldEnableTracking) {
      posthog.identify(userId, { locale });
    }
  };

  const captureTrackingEvent = ({
    eventName,
    properties,
  }: {
    eventName: PosthogTrackingEventName;
    properties: Record<string, unknown> & { source: PosthogTrackingSource };
  }) => {
    if (shouldEnableTracking) {
      posthog.capture(eventName, properties);
    }
  };

  const captureUserProperties = ({
    properties,
    setOnce = false,
  }: CaptureUserProperties) => {
    if (shouldEnableTracking) {
      const payload = setOnce
        ? [undefined, properties]
        : [properties, undefined];
      posthog.setPersonProperties(...payload);
    }
  };

  return {
    identifyUser,
    captureTrackingEvent,
    captureUserProperties,
  };
};
